<script setup lang="ts">
import RegionsStatisticsSection from '@/components/region-statistics/RegionsStatisticsSection.vue'
import { FuelTypeCount, VehicleCountData } from '@/types/statistics'
import { RegionType } from '~/types/types'
import { ChartDataItem } from '~/types/chart'
import AppLoadingWrapper from '~/components/app/AppLoadingWrapper.vue'
import CardStatWrapper from '~/components/layout/CardStatWrapper.vue'
import FilterSetting from '~/components/data-filters/FilterSetting.vue'
import DataSource from '~/components/data-views/DataSource.vue'
import { useStatisticsStore } from '~/stores/statistics'

const props = defineProps<{
  regionType?: RegionType
  regionId?: string
  hideFilters?: boolean
}>()
const statisticsStore = useStatisticsStore()
const filterSettings = computed(() => statisticsStore.filterSettings)
const vehicleTypeFilterValues = computed(() =>
  statisticsStore.filters.find((f) => f.filterIdentifier === 'vehicleType')?.values?.join(', ')
)
const {
  data: vehicleData,
  filteredData: filteredVehicleData,
  isReady,
} = useVehicleCountDataFetch(props.regionType, props.regionId, true)

const vehicleCount = computed<ChartDataItem[]>(() => {
  if (!filteredVehicleData.value) return []
  return Object.keys(filteredVehicleData.value[filteredVehicleData.value.length - 1].total_count)
    .filter((key) => vehicleKeys.includes(key as keyof FuelTypeCount))
    .map((key) => ({
      label: key,
      value:
        (filteredVehicleData.value &&
          (filteredVehicleData.value[filteredVehicleData.value.length - 1].total_count[
            key as keyof FuelTypeCount
          ] as number)) ||
        0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
})
const vehicleTypeCount = computed(() => (vehicleType: string) => {
  if (!vehicleData.value) return 0
  return (
    vehicleData.value &&
    (vehicleData.value[vehicleData.value.length - 1].vehicle_data.find(
      (data) => data.vehicle_type === vehicleType
    )?.total_count ||
      0)
  )
})

const { filteredData: vehicleForcastData, isReady: forecastDataIsReady } =
  useVehicleForecastDataFetch(props.regionType, props.regionId, true)
const foreCastData = computed(() => {
  if (!vehicleForcastData.value) return []
  return Array.isArray(vehicleForcastData.value)
    ? vehicleForcastData.value
    : [vehicleForcastData.value]
})
</script>

<template>
  <AppLoadingWrapper :is-loading="!isReady" :height="400">
    <div v-if="!hideFilters">
      <p>Filtrera på fordonstyp</p>
      <FilterSetting
        class="my-3"
        content-type="statistics"
        :setting="filterSettings.vehicleTypeFromFleetData"
        :show-label="false"
      />
    </div>
    <AppHeading title="Översikt" type="h3" size="large" />

    <CardStatWrapper>
      <CardStat label="Personbilar" :value="vehicleTypeCount('personbil')" />
      <CardStat label="Lätta lastbilar" :value="vehicleTypeCount('lätt lastbil')" />
      <CardStat label="Tunga lastbilar" :value="vehicleTypeCount('tung lastbil')" />
    </CardStatWrapper>
    <AppHeading
      type="h3"
      size="large"
      :title="`Antal fordon per drivmedel (${
        vehicleData && vehicleData[vehicleData.length - 1]?.year
      })`"
    />
    <p v-if="vehicleTypeFilterValues">
      Fordonstyper: <strong>{{ vehicleTypeFilterValues }}</strong>
    </p>
    <div class="my-4"><ChartBar :data="vehicleCount" direction="horizontal" /></div>
    <DataSource :source-data="[{ identifier: 'trafa' }]" />
    <div class="my-4">
      <TabView class="tabs" :active-index="1">
        <TabPanel header="Fordonshistorik">
          <div class="mt-2">
            <p>Grafen visar historisk utveckling av fordonsflottan från 2010.</p>
            <div class="mt-2">
              <p v-if="vehicleTypeFilterValues">
                Fordonstyper: <strong>{{ vehicleTypeFilterValues }}</strong>
              </p>
              <GraphVehicleCount :graph-data="filteredVehicleData" />
            </div>
            <DataSource :source-data="[{ identifier: 'trafa' }]" />
          </div>
        </TabPanel>
        <TabPanel header="Fordonsprognos">
          <AppLoadingWrapper :is-loading="!forecastDataIsReady" :height="200">
            <div class="mt-2">
              <p>Grafen visar prognostiserad utveckling av fordonsflottan fram till 2035.</p>
              <p>
                <span v-if="vehicleTypeFilterValues"
                  >Fordonstyper: <strong>{{ vehicleTypeFilterValues }}</strong
                  ><br
                /></span>
                <span class="warning">OBS. Endast prognos för personbilar finns tillgänglig</span>
              </p>
              <GraphVehicleCount v-if="vehicleForcastData" :graph-data="foreCastData" />
              <DataSource :source-data="[{ identifier: 'vti' }]" />
            </div>
          </AppLoadingWrapper>
        </TabPanel>
      </TabView>
    </div>
  </AppLoadingWrapper>
</template>

<style lang="scss" scoped>
@import 'App.scss';
.warning {
  background: $warning;
  font-size: 0.75rem;
}
</style>
```
